import { createFileRoute, Link } from "@tanstack/react-router"
import { useState } from "react"
import { ArrowUpRight, Search, Compass, Rocket, ShieldCheck, Check } from "lucide-react"
import { useServices } from "@/hooks/useServices"
import { AppIcon } from "@/lib/icon"
import { TechIcon } from "@/components/TechIcon"
import { SectionSkeleton, ErrorState } from "@/components/state"
import { pageMeta } from "@/lib/seo"

export const Route = createFileRoute("/_public/services")({
  component: Services,
  head: () =>
    pageMeta({
      title: "Services — Nexovia IT",
      description: "Software, cloud and AI services delivered by Nexovia IT.",
      path: "/services",
    }),
})

const process = [
  { n: "01", icon: Search, title: "Discover", body: "Align on outcomes, constraints and success metrics." },
  { n: "02", icon: Compass, title: "Design", body: "Architecture and UX decisions validated before code." },
  { n: "03", icon: Rocket, title: "Build", body: "Weekly ship cycles with continuous feedback." },
  { n: "04", icon: ShieldCheck, title: "Scale", body: "Observability, performance and hardening for growth." },
]

const techGroups: Record<string, string[]> = {
  All: [
    "OpenAI", "TensorFlow", "React", "Next.js", "Node", "Angular", "Python", "PostgreSQL", "MongoDB", "AWS",
    "GCP", "Azure", "Docker", "Kubernetes", "TypeScript", "Swift", "Laravel", "PHP", "Yii 2", "Django", "FastAPI",
  ],
  "AI / ML": ["OpenAI", "TensorFlow", "PyTorch", "Hugging Face", "LangChain", "Pinecone"],
  Frontend: ["React", "Next.js", "TanStack", "Angular", "Vue", "TypeScript", "Tailwind"],
  Backend: ["Node", "Python", "Go", "Rust", "NestJS", "GraphQL", "Laravel", "PHP", "Yii 2", "Django", "FastAPI", "Express"],
  Databases: ["PostgreSQL", "MongoDB", "Redis", "Snowflake", "BigQuery", "Elasticsearch"],
  "Cloud / DevOps": ["AWS", "GCP", "Azure", "Docker", "Kubernetes", "Terraform"],
}

const stats = [
  { k: "5,000+", v: "Users served" },
  { k: "6+", v: "Years of experience" },
  { k: "98%", v: "Client satisfaction" },
  { k: "300+", v: "Projects delivered" },
]

function Services() {
  const [tab, setTab] = useState<keyof typeof techGroups>("All")
  const { data: services, isLoading, isError } = useServices()
  const activeServices = (services ?? []).filter((s) => s.is_active)

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{ background: "radial-gradient(ellipse at top, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 60%)" }}
        />
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-rust">
            <span className="h-1.5 w-1.5 rounded-full bg-rust" /> Software · Cloud · AI Services
          </span>
          <h1 className="mx-auto mt-6 max-w-4xl text-5xl md:text-7xl font-bold text-balance">
            Everything You Need to <span className="text-rust">Ship and Scale.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            End-to-end software, cloud and AI services designed to help startups and enterprises launch, scale and lead globally.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Book a Free Strategy Call <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/portfolio" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:bg-secondary">
              View our work
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Our services</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold">Everything Your Business Needs</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            One senior team, many disciplines — everything you need to build, launch and scale a modern software business.
          </p>
        </div>
        <div className="mt-14">
          {isLoading ? (
            <SectionSkeleton rows={6} className="md:grid-cols-2 lg:grid-cols-3" />
          ) : isError ? (
            <ErrorState message="Couldn't load services right now." />
          ) : activeServices.length === 0 ? (
            <p className="text-center text-muted-foreground">No services published yet.</p>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {activeServices.map((s) => {
                return (
                  <article
                    key={s.id}
                    className="group relative flex flex-col rounded-2xl border border-border bg-card p-8 transition hover:-translate-y-1 hover:shadow-card"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-ink text-cream group-hover:bg-gradient-accent transition">
                      <AppIcon name={s.icon} className="h-5 w-5" />
                    </div>
                    <h3 className="mt-6 text-xl font-semibold">{s.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.description}</p>
                    {s.features.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {s.features.map((feature) => (
                          <li key={feature} className="flex items-start gap-2 text-sm">
                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-rust" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Link
                      to={s.cta_text ? s.cta_link ?? "/contact" : "/contact"}
                      className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-rust hover:text-rust-deep"
                    >
                      {s.cta_text || "Get started"} <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </article>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-y border-border bg-secondary/30">
        <div className="mx-auto max-w-5xl px-6 py-24 md:py-28">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-rust">Process</p>
            <h2 className="mt-3 text-4xl md:text-5xl font-bold">How We Deliver Excellence</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              A proven delivery model that keeps quality high and timelines honest.
            </p>
          </div>

          <ol className="mt-14 relative border-l-2 border-dashed border-border pl-8 md:pl-14 space-y-10">
            {process.map((p) => (
              <li key={p.n} className="relative">
                <span className="absolute -left-[42px] md:-left-[60px] flex h-10 w-10 items-center justify-center rounded-full bg-ink text-cream font-display text-sm">
                  {p.n}
                </span>
                <div className="flex items-start gap-3">
                  <p.icon className="h-5 w-5 text-rust mt-1" />
                  <div>
                    <h3 className="text-xl font-semibold">{p.title}</h3>
                    <p className="mt-1 text-muted-foreground">{p.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TECH STACK */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Tech stack</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold text-balance">
            Powered by Best-in-Class <span className="text-rust">Technologies</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            We use battle-tested tools chosen for the job — never for hype.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-2">
          {Object.keys(techGroups).map((k) => (
            <button
              key={k}
              onClick={() => setTab(k as keyof typeof techGroups)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                tab === k ? "bg-ink text-cream" : "border border-border bg-background hover:bg-secondary"
              }`}
            >
              {k}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {techGroups[tab].map((t) => (
            <div
              key={t}
              className="rounded-2xl border border-border bg-card px-4 py-6 text-center transition hover:border-rust hover:shadow-card"
            >
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-accent text-cream">
                <TechIcon name={t} className="h-5 w-5" />
              </div>
              <div className="mt-3 text-sm font-semibold">{t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border bg-ink text-cream">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-cream/10 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.v} className="bg-ink px-6 py-12 text-center">
              <div className="font-display text-4xl md:text-5xl text-rust-glow">{s.k}</div>
              <div className="mt-2 text-sm text-cream/70">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-6 py-24 md:py-28">
        <div className="rounded-3xl border border-border bg-card p-10 md:p-16 text-center shadow-card">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Start today</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold">Ready to Build Something Amazing?</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Join the teams that trust Nexovia IT to engineer, launch and scale their digital products.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Book a free consultation <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/about" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold hover:bg-secondary">
              Explore our process
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
